import { Button, PhoneInput, TextField, Typography } from '@themenu/design-system'
import { type ChangeEvent, type FC, type FormEvent, useState } from 'react'
import { useIntl } from 'react-intl'

import { ActivationStepLayout } from './ActivationStepLayout'

/**
 * Entry form for the email (step 4) and phone (step 5) steps. A single component because both
 * are a labelled TextField + primary submit; the OTP verification that follows
 * is handled by the shared `ActivationOtpForm`. The funnel only reaches this
 * step when the channel is missing (phone) or missing/`@nomail.themenu.com.br`
 * (email), as decided server-side.
 */
export type ContactEntryStepProps = {
  kind: 'phone' | 'email'
  initialValue?: string
  onSubmit: (value: string) => Promise<void>
  errorMessageId?: string | null
  showBackButton?: boolean
  onBack?: () => void
}

export const ContactEntryStep: FC<ContactEntryStepProps> = ({
  kind,
  initialValue = '',
  onSubmit,
  errorMessageId = null,
  showBackButton = false,
  onBack,
}) => {
  const { formatMessage, locale } = useIntl()
  const phoneLocale = locale.startsWith('en') ? 'en' : locale.startsWith('pt') ? 'pt-BR' : 'fr'
  const [value, setValue] = useState(initialValue)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!value.trim() || submitting) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit(value.trim())
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ActivationStepLayout
      titleId={`br_activation.${kind}.title`}
      errorMessageId={errorMessageId}
      showBackButton={showBackButton}
      onBack={onBack}
      onSubmit={handleSubmit}
      testId={`br-activation-${kind}-form`}
      footer={
        <Button
          data-testid={`br-activation-${kind}-submit`}
          type="submit"
          variant="primary"
          className="w-full"
          disabled={!value.trim() || submitting}
          loading={submitting}
        >
          {formatMessage({ id: `br_activation.${kind}.submit` })}
        </Button>
      }
    >
      <Typography variant="body">{formatMessage({ id: `br_activation.${kind}.description` })}</Typography>
      {kind === 'phone' ? (
        <PhoneInput
          size={56}
          id="br-activation-phone"
          name="phone"
          data-testid="br-activation-phone"
          label={formatMessage({ id: 'br_activation.phone.label' })}
          value={value as any}
          onChange={(v) => setValue(v ?? '')}
          country="BR"
          locale={phoneLocale}
          autoFocus
        />
      ) : (
        <TextField
          size={56}
          id={`br-activation-${kind}`}
          name={kind}
          data-testid={`br-activation-${kind}`}
          label={formatMessage({ id: `br_activation.${kind}.label` })}
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          autoFocus
          autoCapitalize="off"
          spellCheck="false"
          type="email"
          inputMode="email"
        />
      )}
    </ActivationStepLayout>
  )
}
